import React, { useEffect, useMemo, useRef } from 'react';
import {
  buildDialPlan,
  angleAtSourceTime,
  dialGeometry,
  drawSpeedDial,
} from '../lib/speedDial';
import './SpeedDialOverlay.css';

// The live preview of MotionPace's burned-in speed dial (#533).
//
// WHY A PREVIEW EXISTS AT ALL. The dial is burned into the pixels by the worker
// (#531), so without this the only way to find out what it looks like on your
// own footage is to spend a render on it. This draws the same dial on a canvas
// over the editor's player, from the SAME persisted speed curve the worker
// renders from, so the switch answers its own question instantly.
//
// IT IS DRIVEN OFF THE SOURCE CLOCK, because the editor's player is showing the
// SOURCE video (EditorPanel passes `videoUrl`, the uploaded footage) and not a
// paced render. `angleAtSourceTime` inverts the curve — playhead -> source frame
// -> the output frame that will show it -> that frame's angle — so at every
// instant the preview shows exactly the angle the render will show over exactly
// this footage. Driving it off elapsed preview time instead would desync the
// dial from the picture: it would read 6x while the busy stretch was on screen.
//
// A CONSEQUENCE WORTH KNOWING: on unpaced source footage playing at 1x the
// sweep turns at a constant rate, because the dial's rate is DEFINED as one
// revolution per REVOLUTION_SECONDS of source time. Speed becomes visible in
// the render only because the render compresses source time. No overlay on
// unpaced footage can show the speed variation and stay synchronised with the
// picture; this previews the dial's look, position and size, not its cadence.
//
// RAF, NOT `timeupdate`. The video element fires `timeupdate` about four times
// a second, which would step the sweep in visible ~45-degree jumps. The loop
// below reads `currentTime` every animation frame instead, which also makes
// scrubbing and seeking free — there is no event to miss, the playhead is
// simply read wherever it now is.
const SpeedDialOverlay = ({ videoRef, speedCurve, watermarkPosition = null }) => {
  const canvasRef = useRef(null);
  const plan = useMemo(() => buildDialPlan(speedCurve), [speedCurve]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !plan) return undefined;
    // jsdom has no canvas implementation, so this is null under test. The
    // component still mounts and still lays out; it just draws nothing, which
    // is why the geometry and angle maths live in lib/speedDial.js where they
    // can be tested directly, and the pixels are verified in a real browser.
    const ctx = canvas.getContext ? canvas.getContext('2d') : null;
    if (!ctx) return undefined;

    let frame = 0;
    let lastAngle = null;
    let lastWidth = 0;
    let lastHeight = 0;
    let lastRatio = 0;

    const tick = () => {
      frame = requestAnimationFrame(tick);
      const box = canvas.parentElement;
      if (!box) return;

      const ratio = window.devicePixelRatio || 1;
      const width = box.clientWidth;
      const height = box.clientHeight;
      if (width <= 0 || height <= 0) return;

      const angle = angleAtSourceTime(plan, videoRef?.current?.currentTime);
      // Both guards, because either alone leaves a stale picture: a paused
      // video never changes its angle but its player still reflows, and a
      // steady player still has to follow the playhead.
      //
      // `ratio` is tracked alongside the box size because it scales the BACKING
      // STORE, not the layout: dragging the window to a display of a different
      // DPI changes neither `width`/`height` nor the angle, so without this the
      // paused loop would early-return forever and leave `canvas.width` at the
      // old device-pixel scale — a soft (or over-sharp) dial until the playhead
      // next moved.
      const resized = width !== lastWidth || height !== lastHeight || ratio !== lastRatio;
      if (!resized && angle === lastAngle) return;
      lastWidth = width;
      lastHeight = height;
      lastRatio = ratio;
      lastAngle = angle;

      if (resized) {
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
      }
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
      ctx.clearRect(0, 0, width, height);

      const geometry = dialGeometry(width, height, watermarkPosition);
      if (!geometry) return;
      drawSpeedDial(ctx, { ...geometry, angleDeg: angle });
    };

    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [plan, videoRef, watermarkPosition]);

  if (!plan) return null;
  return (
    <canvas
      ref={canvasRef}
      className="tw-speed-dial"
      data-testid="speed-dial-overlay"
      // Decoration over a video that already has its own controls — there is
      // nothing here for a screen reader to do, and the same information is in
      // the pace chart's numbers.
      aria-hidden="true"
    />
  );
};

export default SpeedDialOverlay;
